import { makeAutoObservable, runInAction } from "mobx";
import { db } from "@stores/environments/Database.web";
import { ref, get, set, update, child } from "firebase/database";
import { IPush } from "@interfaces/ipush";
import { logService } from "../services/LogService";
import { authStore } from "./AuthStore";

class PushStore {
  pushes: IPush[] = [];
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchPushes = async () => {
    this.loading = true;
    const dbRef = ref(db);

    try {
      const snapshot = await get(child(dbRef, "pushes"));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const allPushes: IPush[] = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        runInAction(() => {
          this.pushes = allPushes.filter(push => !push.deleted);
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.pushes = [];
          this.loading = false;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar pushes:", error);
      runInAction(() => {
        this.error = "Erro ao carregar pushes.";
        this.loading = false;
      });
    }
  };

  savePush = async (push: IPush) => {
    const dbRef = ref(db, `pushes/${push.id}`);
    
    // Busca o valor anterior para verificar se é update ou create
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }
    
    const isUpdate = !!oldValue;
    
    await set(dbRef, push);
    
    // Registra o log
    if (authStore._user) {
      const logData = isUpdate 
        ? logService.updateLogData(
            'Push',
            push.id,
            `pushes/${push.id}`,
            oldValue,
            push,
            `Push ${isUpdate ? 'atualizado' : 'criado'}: ${push.title}`
          )
        : logService.createLogData(
            'Push',
            push.id,
            `pushes/${push.id}`,
            push,
            `Push criado: ${push.title}`
          );
      
      await logService.logOperation(logData);
    }
    
    this.fetchPushes(); // Recarrega a lista após salvar
  };

  deletePush = async (id: string) => {
    const confirmed = window.confirm("Tem certeza que deseja excluir este push?");
    if (!confirmed) {
      return;
    }

    // Busca o valor anterior para o log
    const dbRef = ref(db, `pushes/${id}`);
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }

    const deleteRef = ref(db, `pushes/${id}/deleted`);
    await set(deleteRef, true);
    
    // Registra o log
    if (authStore._user && oldValue) {
      const logData = logService.deleteLogData(
        'Push',
        id,
        `pushes/${id}`,
        oldValue,
        `Push excluído: ${oldValue.title}`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchPushes(); // Recarrega a lista após deletar
  };

  toggleActive = async (id: string, active: boolean) => {
    const dbRef = ref(db, `pushes/${id}`);
    
    // Busca o valor anterior para o log
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }
    
    await update(dbRef, { active });
    
    // Registra o log
    if (authStore._user && oldValue) {
      const newValue = { ...oldValue, active };
      const logData = logService.toggleLogData(
        'Push',
        id,
        `pushes/${id}`,
        oldValue,
        newValue,
        `Push ${active ? 'ativado' : 'desativado'}: ${oldValue.title}`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchPushes();
  };
}

export const pushStore = new PushStore();
